// Estatisticas agregadas de uma serie temporal pro StatsPanel do
// LineChart (modo avancado). Calcula tudo em UMA passagem dos dados
// quando possivel — chamado com 10k+ pontos em chart pesado.
//
// Input: data = [{ timestamp, value }] (timestamp em segundos, value
// numerico). ranges = [{ category, max, color }] (referenceLines da
// variavel — usados pra calcular tempo em cada zona).

// Helper: classifica um valor numa categoria de range. Ranges sao
// ordenados por max ascendente; primeiro cujo max >= value vence.
// Retorna { category, color } ou null se sem ranges.
const classifyValue = (value, sortedRanges) => {
  if (!sortedRanges || !sortedRanges.length) return null;
  for (let i = 0; i < sortedRanges.length; i += 1) {
    if (value <= sortedRanges[i].value) {
      return {
        category: sortedRanges[i].label || sortedRanges[i].category,
        color: sortedRanges[i].color,
      };
    }
  }
  // Acima do threshold mais alto — usa cor/label do ultimo range
  // (semantica "ainda em erro / overflow do worst case")
  const last = sortedRanges[sortedRanges.length - 1];
  return {
    category: last.label || last.category,
    color: last.color,
  };
};

// Percentil simples por interpolacao linear. p e fracao [0, 1].
const percentile = (sortedValues, p) => {
  if (!sortedValues.length) return null;
  if (sortedValues.length === 1) return sortedValues[0];
  const rank = p * (sortedValues.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sortedValues[lo];
  const frac = rank - lo;
  return sortedValues[lo] * (1 - frac) + sortedValues[hi] * frac;
};

export const calcChartStats = (data, ranges) => {
  if (!data || !data.length) return null;
  // Filtra valores finitos e extrai os pares timestamp+value pra uso
  // posterior (achar quando ocorreu min/max).
  const pairs = [];
  let sum = 0;
  let count = 0;
  let min = Infinity;
  let max = -Infinity;
  let minPair = null;
  let maxPair = null;

  for (let i = 0; i < data.length; i += 1) {
    const v = Number(data[i].value);
    if (!Number.isFinite(v)) continue; // eslint-disable-line no-continue
    pairs.push({ timestamp: data[i].timestamp, value: v });
    sum += v;
    count += 1;
    if (v < min) {
      min = v;
      minPair = data[i];
    }
    if (v > max) {
      max = v;
      maxPair = data[i];
    }
  }

  if (count === 0) return null;

  const mean = sum / count;

  // Variancia em segunda passagem (Welford daria mais precisao, mas
  // pra UI nao vale a complexidade).
  let sqDiff = 0;
  for (let i = 0; i < pairs.length; i += 1) {
    const d = pairs[i].value - mean;
    sqDiff += d * d;
  }
  const variance = sqDiff / count;
  const stddev = Math.sqrt(variance);

  // Ordena valores pra percentis (clone — nao modifica pairs)
  const sortedValues = pairs.map((p) => p.value).sort((a, b) => a - b);
  const median = percentile(sortedValues, 0.5);
  const q1 = percentile(sortedValues, 0.25);
  const q3 = percentile(sortedValues, 0.75);
  const iqr = q3 - q1;

  // Tempo em cada zona (so se houver ranges definidos). Aproximacao:
  // cada amostra representa um "tick" de tempo igual ao intervalo entre
  // amostras adjacentes. Em vez de calcular delta real por amostra (caro
  // e ruidoso), usamos PROPORCAO de amostras × duracao total — mesma
  // ordem de grandeza, mais simples.
  const sortedRanges = ranges
    ? [...ranges].sort((a, b) => a.value - b.value)
    : null;

  const zoneBreakdown = {}; // { [category]: { count, color, pct } }
  if (sortedRanges && sortedRanges.length) {
    for (let i = 0; i < pairs.length; i += 1) {
      const cls = classifyValue(pairs[i].value, sortedRanges);
      if (!cls) continue; // eslint-disable-line no-continue
      if (!zoneBreakdown[cls.category]) {
        zoneBreakdown[cls.category] = {
          count: 0,
          color: cls.color,
          category: cls.category,
        };
      }
      zoneBreakdown[cls.category].count += 1;
    }
    Object.keys(zoneBreakdown).forEach((cat) => {
      zoneBreakdown[cat].pct = (zoneBreakdown[cat].count / count) * 100;
    });
  }

  // Duracao da janela (primeiro vs ultimo timestamp), em ms.
  const firstTsMs = pairs[0].timestamp * 1000;
  const lastTsMs = pairs[pairs.length - 1].timestamp * 1000;
  const totalSpanMs = lastTsMs - firstTsMs;

  // Detecta outliers via z-score (3 sigma). Threshold classico de
  // "improvavel sob distribuicao normal". Marca o ponto e classifica
  // se foi ALTO (acima de mean + 3σ) ou BAIXO (abaixo). Util pra
  // operador entender se o sensor cuspiu spike (alto) ou dip (baixo).
  // Skippa se stddev ~= 0 (variavel constante — qualquer variacao
  // seria infinitamente improvavel, mas nao e outlier real).
  const Z_THRESHOLD = 3;
  const outliers = [];
  if (stddev > 1e-9) {
    for (let i = 0; i < pairs.length; i += 1) {
      const z = (pairs[i].value - mean) / stddev;
      if (Math.abs(z) > Z_THRESHOLD) {
        outliers.push({
          timestamp: pairs[i].timestamp,
          value: pairs[i].value,
          zScore: z,
          direction: z > 0 ? 'high' : 'low',
        });
      }
    }
  }
  // Set de timestamps pra lookup O(1) quando o chart for renderizar
  // os dots — evita scan no array a cada ponto.
  const outlierTimestamps = new Set(outliers.map((o) => o.timestamp));

  // Regressao linear simples (least squares) — diz a tendencia da
  // variavel ao longo do periodo. x = offset em segundos a partir do
  // primeiro ponto (evita numeros gigantes); y = value. Slope = b1 em
  // y = b0 + b1*x. Convertemos pra "unit/hora" no final (mais legivel
  // pro operador que "unit/segundo").
  const firstTsSec = pairs[0].timestamp;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  for (let i = 0; i < pairs.length; i += 1) {
    const x = pairs[i].timestamp - firstTsSec;
    const y = pairs[i].value;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }
  const n = pairs.length;
  const denom = n * sumXX - sumX * sumX;
  let slopePerSec = 0;
  let intercept = mean;
  if (Math.abs(denom) > 1e-9) {
    slopePerSec = (n * sumXY - sumX * sumY) / denom;
    intercept = (sumY - slopePerSec * sumX) / n;
  }
  // Slope em unit/hora — mais intuitivo (operador pensa em "deriva por
  // hora" mais do que "por segundo").
  const slopePerHour = slopePerSec * 3600;

  // y predito nas pontas da serie — usado pra desenhar a linha de
  // tendencia overlay no chart.
  const trendStartValue = intercept;
  const trendEndValue = intercept + slopePerSec * (pairs[pairs.length - 1].timestamp - firstTsSec);

  return {
    count,
    mean,
    median,
    stddev,
    variance,
    min,
    max,
    range: max - min,
    q1,
    q3,
    iqr,
    minTimestamp: minPair ? minPair.timestamp : null,
    maxTimestamp: maxPair ? maxPair.timestamp : null,
    totalSpanMs,
    // zones: array ordenada por count desc (zona dominante primeiro)
    zones: Object.values(zoneBreakdown).sort((a, b) => b.count - a.count),
    // Tendencia: slope em unit/hora + valores nas pontas pra plotar
    trend: {
      slopePerHour,
      slopePerSec,
      intercept,
      startValue: trendStartValue,
      endValue: trendEndValue,
    },
    // Forecast simples baseado em extrapolacao do trend linear +
    // intervalo de confianca crescente com horizonte (residual stddev
    // * sqrt(h)). Util pra alertas preditivos: 'no ritmo atual, a
    // temperatura cruza o threshold em ~6h'. Nao e modelo robusto —
    // operador interpreta como guia, nao garantia. Skip se variancia
    // zero (variavel constante — projeção sem sentido).
    forecast:
      stddev > 1e-9 && Number.isFinite(trendEndValue)
        ? {
            in1h: {
              hoursAhead: 1,
              value: trendEndValue + slopePerHour * 1,
              ci: stddev * Math.sqrt(1),
            },
            in4h: {
              hoursAhead: 4,
              value: trendEndValue + slopePerHour * 4,
              ci: stddev * Math.sqrt(4),
            },
            in24h: {
              hoursAhead: 24,
              value: trendEndValue + slopePerHour * 24,
              ci: stddev * Math.sqrt(24),
            },
          }
        : null,
    // Outliers detectados (z-score > 3). Quando outliers.length > 0,
    // operador deve investigar — sensor pode ter cuspido valores
    // anomalos OU houve evento real (trip, dip de carga, etc.).
    outliers,
    outlierCount: outliers.length,
    outlierHighCount: outliers.filter((o) => o.direction === 'high').length,
    outlierLowCount: outliers.filter((o) => o.direction === 'low').length,
    outlierTimestamps,
    // Change-points: mudancas de regime detectadas via sliding window
    // mean shift. windowSize escolhido proporcional ao tamanho da
    // serie pra escalar bem (mais amostras = janela maior, suaviza
    // ruido). Lista vazia quando serie e muito curta ou estavel.
    changePoints: detectChangePoints(pairs, {
      windowSize: Math.max(20, Math.floor(pairs.length / 100)),
      sigmaThreshold: 2.5,
    }),
  };
};

// Detecta segmentos operacionais (running vs stopped) numa serie
// temporal — geralmente alimentada por PotenciaAtivaMedia ou Vazao
// (variavel de referencia da operacao). 'Running' = |valor| acima
// de threshold; 'Stopped' = abaixo. Threshold default e 1% do pico
// (max absoluto) — funciona pra qualquer escala (kW, L/s, MW).
//
// Pra evitar flicker (alternancias rapidas por ruido de sensor),
// segmentos menores que minDurationMs sao mesclados com o anterior.
//
// Retorna: [{ startTs, endTs, state: 'running'|'stopped', durationMs }]
// + agregados (totalRunningMs, totalStoppedMs, uptimePct, etc.)
export const detectOperationalSegments = (data, options = {}) => {
  const {
    threshold: customThreshold,
    minDurationMs = 5 * 60 * 1000, // 5min default
  } = options;

  if (!data || data.length < 2) {
    return { segments: [], totalRunningMs: 0, totalStoppedMs: 0, uptimePct: 0 };
  }

  // Parse valores e timestamps
  const points = data
    .map((p) => ({
      ts: Number(p.timestamp),
      v: Number(p.value),
    }))
    .filter((p) => Number.isFinite(p.ts) && Number.isFinite(p.v));
  if (points.length < 2) {
    return { segments: [], totalRunningMs: 0, totalStoppedMs: 0, uptimePct: 0 };
  }

  // Threshold: 1% do pico absoluto. Operador pode passar custom.
  let peak = 0;
  for (let i = 0; i < points.length; i += 1) {
    const av = Math.abs(points[i].v);
    if (av > peak) peak = av;
  }
  const threshold =
    customThreshold != null ? customThreshold : Math.max(peak * 0.01, 1e-6);

  // Primeira passada: classifica cada ponto em running/stopped
  // e agrupa em segmentos consecutivos do mesmo estado.
  const segments = [];
  let curState = Math.abs(points[0].v) > threshold ? 'running' : 'stopped';
  let curStart = points[0].ts;
  for (let i = 1; i < points.length; i += 1) {
    const state = Math.abs(points[i].v) > threshold ? 'running' : 'stopped';
    if (state !== curState) {
      // Fecha segmento anterior. endTs = timestamp do ponto que mudou
      // o estado (boundary).
      segments.push({
        startTs: curStart,
        endTs: points[i].ts,
        state: curState,
      });
      curState = state;
      curStart = points[i].ts;
    }
  }
  // Ultimo segmento
  segments.push({
    startTs: curStart,
    endTs: points[points.length - 1].ts,
    state: curState,
  });

  // Segunda passada: merge segments < minDurationMs com o anterior
  // (suaviza flicker). Excecao: NUNCA mesclar se for o unico
  // segmento (resultaria em array vazio).
  const minDurationSec = minDurationMs / 1000;
  const merged = [];
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    const duration = seg.endTs - seg.startTs;
    if (
      merged.length > 0 &&
      duration < minDurationSec &&
      segments.length > 1
    ) {
      // Estende o ultimo merged segment ate cobrir este (assume o
      // estado do anterior — segmento curto e ruido)
      merged[merged.length - 1].endTs = seg.endTs;
    } else {
      merged.push({ ...seg });
    }
  }

  // Calcula durations + agregados
  let totalRunningMs = 0;
  let totalStoppedMs = 0;
  let biggestStoppedMs = 0;
  let stoppedCount = 0;
  merged.forEach((seg) => {
    /* eslint-disable no-param-reassign */
    seg.durationMs = (seg.endTs - seg.startTs) * 1000;
    /* eslint-enable no-param-reassign */
    if (seg.state === 'running') {
      totalRunningMs += seg.durationMs;
    } else {
      totalStoppedMs += seg.durationMs;
      stoppedCount += 1;
      if (seg.durationMs > biggestStoppedMs) biggestStoppedMs = seg.durationMs;
    }
  });
  const totalMs = totalRunningMs + totalStoppedMs;
  const uptimePct = totalMs > 0 ? (totalRunningMs / totalMs) * 100 : 0;

  return {
    segments: merged,
    totalRunningMs,
    totalStoppedMs,
    biggestStoppedMs,
    stoppedCount,
    uptimePct,
    threshold,
  };
};

// Change-point detection via sliding-window mean shift.
// Algoritmo: pra cada ponto candidato, compara media nos W pontos
// ANTES vs DEPOIS. Se a diferenca normalizada pelo desvio padrao
// (significance) passa do threshold, marca como change-point.
// Depois suprime change-points proximos — mantem so o LOCAL MAX
// dentro de janelas W (evita N change-points consecutivos na mesma
// transicao).
//
// Casos de uso: sensor que sobe abruptamente (start de operacao),
// processo que muda de regime estavel, falha que muda assinatura.
// Mais simples que PELT (offline optimal) mas suficiente pra series
// de sensor industrial.
export const detectChangePoints = (data, options = {}) => {
  const {
    windowSize = 30,
    sigmaThreshold = 2.5,
    minSpacing = null, // default: windowSize
  } = options;

  if (!data || data.length < 2 * windowSize + 1) return [];

  const values = [];
  const timestamps = [];
  for (let i = 0; i < data.length; i += 1) {
    const v = Number(data[i].value);
    if (Number.isFinite(v)) {
      values.push(v);
      timestamps.push(data[i].timestamp);
    }
  }
  if (values.length < 2 * windowSize + 1) return [];

  const candidates = [];
  // Pra cada candidato i no meio (longe das bordas), calcula medias
  // antes/depois + desvios pra normalizar diff.
  for (let i = windowSize; i < values.length - windowSize; i += 1) {
    let sumB = 0;
    let sumA = 0;
    for (let j = i - windowSize; j < i; j += 1) sumB += values[j];
    for (let j = i; j < i + windowSize; j += 1) sumA += values[j];
    const meanB = sumB / windowSize;
    const meanA = sumA / windowSize;

    // Pooled stddev — dispersao dentro de cada janela
    let sqB = 0;
    let sqA = 0;
    for (let j = i - windowSize; j < i; j += 1) sqB += (values[j] - meanB) ** 2;
    for (let j = i; j < i + windowSize; j += 1) sqA += (values[j] - meanA) ** 2;
    const pooledVar = (sqB + sqA) / (2 * windowSize - 2);
    const pooledStd = Math.sqrt(Math.max(pooledVar, 1e-12));

    const diff = meanA - meanB;
    const significance = Math.abs(diff) / pooledStd;

    if (significance > sigmaThreshold) {
      candidates.push({
        index: i,
        timestamp: timestamps[i],
        meanBefore: meanB,
        meanAfter: meanA,
        delta: diff,
        significance,
      });
    }
  }

  // Supressao: mantem so picos locais de significance dentro de
  // janelas de tamanho minSpacing. Evita "rajada" de change-points em
  // transicoes que duram alguns pontos.
  const spacing = minSpacing || windowSize;
  const filtered = [];
  for (let i = 0; i < candidates.length; i += 1) {
    let isPeak = true;
    for (let j = 0; j < candidates.length; j += 1) {
      if (i === j) continue; // eslint-disable-line no-continue
      if (Math.abs(candidates[j].index - candidates[i].index) <= spacing) {
        if (candidates[j].significance > candidates[i].significance) {
          isPeak = false;
          break;
        }
      }
    }
    if (isPeak) filtered.push(candidates[i]);
  }
  return filtered;
};

// Coeficiente de correlacao de Pearson entre duas series de mesmo
// tamanho. Retorna r in [-1, 1] ou null se inviavel (n<2 ou
// variancia zero — uma serie constante nao correlaciona com nada).
// r > 0: variaveis se movem juntas; r < 0: inversamente; |r| -> 1
// mais forte. Usado pelo modo avancado pra mostrar relacao entre
// primaria e compareSeries.
export const computeCorrelation = (xs, ys) => {
  const n = Math.min(xs.length, ys.length);
  if (n < 2) return null;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  let sumYY = 0;
  for (let i = 0; i < n; i += 1) {
    const x = xs[i];
    const y = ys[i];
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
    sumYY += y * y;
  }
  const num = n * sumXY - sumX * sumY;
  const denomX = Math.sqrt(n * sumXX - sumX * sumX);
  const denomY = Math.sqrt(n * sumYY - sumY * sumY);
  if (denomX < 1e-12 || denomY < 1e-12) return null;
  const r = num / (denomX * denomY);
  // Clamp pra [-1, 1] pra cobrir floating-point overflow
  return Math.max(-1, Math.min(1, r));
};

// Classifica r em rotulos legiveis pra UI. Threshold Cohen-like
// adaptado pra contexto industrial (sensores tem ruido alto, entao
// |r| > 0.5 ja conta como moderado significativo).
export const classifyCorrelation = (r) => {
  if (!Number.isFinite(r)) {
    return { strength: '—', direction: '', abs: 0 };
  }
  const abs = Math.abs(r);
  let strength;
  if (abs < 0.3) strength = 'fraca';
  else if (abs < 0.5) strength = 'moderada';
  else if (abs < 0.8) strength = 'forte';
  else strength = 'muito forte';
  const direction = r >= 0 ? 'positiva' : 'negativa';
  return { strength, direction, abs };
};

// Formata duracao em ms pra string legivel (ex.: "2h 14min", "5min",
// "32s"). Usado no StatsPanel pra mostrar tempo em cada zona.
export const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return '0s';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const totalMin = Math.round(totalSec / 60);
  if (totalMin < 60) return `${totalMin}min`;
  const hours = Math.floor(totalMin / 60);
  const remMin = totalMin % 60;
  if (hours < 24) return remMin > 0 ? `${hours}h ${remMin}min` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remH = hours % 24;
  return remH > 0 ? `${days}d ${remH}h` : `${days}d`;
};
